/**
 * Zoom persistence for the Windows (WinAppSDK) app.
 *
 * The WinUI 3 WebView2 control does not expose CoreWebView2Controller.ZoomFactor,
 * so the shell cannot save or restore the browser's native zoom. Instead, when
 * running inside the shell, the frontend owns zoom: Ctrl+wheel and Ctrl+Plus/
 * Minus/0 are intercepted and applied as CSS zoom on the root element, persisted
 * per machine in localStorage. The shell disables the browser's built-in zoom
 * (CoreWebView2.Settings.IsZoomControlEnabled = false) so the two mechanisms
 * cannot stack.
 *
 * Browser users are untouched — browsers already persist per-site zoom natively,
 * and this module only activates inside a WebView2 host (window.chrome.webview).
 * index.html re-applies the saved zoom inline before first paint to avoid a
 * 100% flash; this module handles interaction from then on.
 */

const STORAGE_KEY = 'OrnaWindowsAppZoomFactor';

// Chromium's zoom ladder, capped at 3x.
const ZOOM_LEVELS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];

const PILL_VISIBLE_MS = 2000;
const PILL_FADE_MS = 300;

let currentZoom = 1;
let pillEl: HTMLDivElement | null = null;
let pillHideTimer: ReturnType<typeof setTimeout> | null = null;
let pillRemoveTimer: ReturnType<typeof setTimeout> | null = null;

function isWindowsShell(): boolean {
  return typeof (window as any).chrome?.webview?.postMessage === 'function';
}

function nearestZoomIndex(value: number): number {
  let best = ZOOM_LEVELS.indexOf(1);
  let bestDelta = Number.MAX_VALUE;
  for (let i = 0; i < ZOOM_LEVELS.length; i++) {
    const delta = Math.abs(ZOOM_LEVELS[i] - value);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  }
  return best;
}

function applyZoom(zoom: number, showIndicator: boolean): void {
  currentZoom = zoom;
  // An empty string at 100% keeps the root style clean for the default case.
  document.documentElement.style.zoom = zoom === 1 ? '' : String(zoom);
  try {
    localStorage.setItem(STORAGE_KEY, String(zoom));
  } catch { /* storage unavailable — zoom still applies for this session */ }
  if (showIndicator) {
    showZoomPill(zoom);
  }
}

function stepZoom(direction: 1 | -1): void {
  const index = nearestZoomIndex(currentZoom) + direction;
  if (index < 0 || index >= ZOOM_LEVELS.length) {
    // Already at the ladder edge — still show the pill so the keypress has feedback.
    showZoomPill(currentZoom);
    return;
  }
  applyZoom(ZOOM_LEVELS[index], true);
}

function showZoomPill(zoom: number): void {
  if (!pillEl) {
    pillEl = document.createElement('div');
    pillEl.id = 'orna-zoom-pill';
    pillEl.setAttribute('role', 'status');
    pillEl.style.cssText =
      'position:fixed;bottom:20px;left:20px;z-index:100000;' +
      'background:rgba(32,32,32,0.92);color:#fff;padding:6px 14px;' +
      'border-radius:16px;font:13px/1.4 "Segoe UI",Arial,sans-serif;' +
      `pointer-events:none;opacity:0;transition:opacity ${PILL_FADE_MS}ms ease;`;
    document.body.appendChild(pillEl);
  }
  if (pillHideTimer) clearTimeout(pillHideTimer);
  if (pillRemoveTimer) clearTimeout(pillRemoveTimer);

  const percent = Math.round(zoom * 100);
  pillEl.textContent = zoom === 1 ? 'Zoom: 100%' : `Zoom: ${percent}% (Ctrl+0 to reset)`;
  // Force a style flush so the opacity transition runs even on a freshly created pill.
  void pillEl.offsetWidth;
  pillEl.style.opacity = '1';

  pillHideTimer = setTimeout(() => {
    if (pillEl) pillEl.style.opacity = '0';
    pillRemoveTimer = setTimeout(() => {
      if (pillEl) {
        pillEl.remove();
        pillEl = null;
      }
    }, PILL_FADE_MS);
  }, PILL_VISIBLE_MS);
}

function onWheel(e: WheelEvent): void {
  if (!e.ctrlKey) return;
  // Google Maps ('greedy' gesture handling) consumes wheel events itself —
  // let the map zoom, exactly as a browser would.
  const target = e.target as Element | null;
  if (target && typeof target.closest === 'function' && target.closest('.gm-style')) return;
  e.preventDefault();
  if (e.deltaY === 0) return;
  stepZoom(e.deltaY < 0 ? 1 : -1);
}

function onKeyDown(e: KeyboardEvent): void {
  if (!e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.key === '+' || e.key === '=') {
    e.preventDefault();
    stepZoom(1);
  } else if (e.key === '-' || e.key === '_') {
    e.preventDefault();
    stepZoom(-1);
  } else if (e.key === '0') {
    e.preventDefault();
    applyZoom(1, true);
  }
}

function restoreSavedZoom(): void {
  let saved: number = NaN;
  try {
    saved = parseFloat(localStorage.getItem(STORAGE_KEY) ?? '');
  } catch { /* storage unavailable */ }
  if (!isFinite(saved) || saved <= 0) {
    currentZoom = 1;
    return;
  }
  // Snap to the ladder so stepping stays aligned even if the stored value drifts.
  const zoom = ZOOM_LEVELS[nearestZoomIndex(saved)];
  currentZoom = zoom;
  document.documentElement.style.zoom = zoom === 1 ? '' : String(zoom);
}

export function initWindowsAppZoom(): void {
  if (!isWindowsShell()) return;
  restoreSavedZoom();
  // Capture phase + non-passive so preventDefault reliably blocks any residual
  // native zoom handling in shells that have not disabled IsZoomControlEnabled yet.
  window.addEventListener('wheel', onWheel, { passive: false, capture: true });
  window.addEventListener('keydown', onKeyDown, true);
}

initWindowsAppZoom();
